import React, { useEffect, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import Loader from "../../../components/loader/Loader";
import { useForm } from "react-hook-form";
import EditIcon from "@mui/icons-material/Edit";
import { yupResolver } from "@hookform/resolvers/yup";
import { grey, red } from "@mui/material/colors";
import SaveIcon from "@mui/icons-material/Save";
import { shopSchema } from "../../../app/schema";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  handlePageSettings,
  hideConfirmModal,
  showConfirmModal,
  showModal,
} from "../../../redux/counterSlice";
import { FormInputText } from "../../../components/form-components/FormInputText";
import { FormInputDropdown } from "../../../components/form-components/FormInputDropdown";
import { FormInputTextArea } from "../../../components/form-components/FormInputTextArea";
import { Map } from "@mui/icons-material";
import LockResetIcon from "@mui/icons-material/LockReset";
import ForgotPasswordModal from "./Forget-password";
import { usePermission } from "../../../hooks/usePermission";

function Shop() {
  const dispatch = useDispatch();
  const permissions = usePermission("Shop");
  const methods = useForm({
    resolver: yupResolver(shopSchema),
  });
  const {
    pageSettings: {
      shop: { pageSize, pageNo },
    },
  } = useSelector((state) => state.counter);
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = methods;
  const [loader, setLoader] = useState(true);
  const [formData, setFormData] = useState([]);
  const [region, setRegion] = useState([]);

  const [open, setOpen] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [selectedShop, setSelectedShop] = useState(null);
  const hasActionPermission = permissions?.edit || permissions?.delete;

  const loadInitialData = async () => {
    setLoader(true);
    axios
      .all([
        axios.get("/getAllShopDetails"),
        axios.get("/getAllActiveRegionList"),
      ])
      .then(function (response) {
        setFormData(response[0]?.data?.data);
        setRegion(response[1]?.data?.data);
      })
      .catch(function (err) {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
        dispatch(hideConfirmModal({ msg: "" }));
      });
  };

  useEffect(() => {
    loadInitialData();
  }, []);
  const deleteClickHandler = async (id) => {
    try {
      let response;
      if (id) {
        response = await axios.get("/deleteLocationDetails/" + id);
        if (!response?.data?.error) {
          toast.success(response?.data?.message);
          loadInitialData();
          setOpen(false);
          setSelectedRow((prev) => null);
          reset();
        } else {
          let errDesc = "";
          if (response?.data?.data) {
            Object.values(response?.data?.data).map((error, index) => {
              errDesc =
                index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
            });
          } else {
            errDesc = response?.data?.message;
          }

          dispatch(
            showModal({
              msg: errDesc,
              error: response?.data?.error,
            })
          );
        }
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        })
      );
    } finally {
      setLoader(false);
      setOpen(false);
      setConfirmOpen(false);
    }
  };

  const columns = [
    {
      name: "shop_details_id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => {
          return <p>{+dataIndex + 1}</p>;
        },
      },
    },
    {
      name: "region",
      label: "Region",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const value = formData[dataIndex]?.region?.[0]?.city?.name;
          return value;
        },
      },
    },
    {
      name: "shop_code",
      label: "Shop Code",
    },
    {
      name: "shop_name",
      label: "Shop Name",
    },
    {
      name: "pincode",
      label: "Pincode",
    },
    {
      name: "mobile_no",
      label: "Mobile No",
    },
    {
      name: "shop_code",
      label: "Forget Password",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const shop = formData[dataIndex];
          return (
            <Button
              onClick={() => {
                setSelectedShop(shop);
                setForgotPasswordOpen(true);
              }}
            >
              <LockResetIcon sx={{ color: red[500] }} fontSize="medium" />
            </Button>
          );
        },
      },
    },

    {
      name: "address",
      label: "Address",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const address = formData[dataIndex]?.address;

          return (
            <Tooltip title={`${address}`}>
              <IconButton>
                <Map sx={{ color: "orange", fontSize: "20px" }} />
              </IconButton>
            </Tooltip>
          );
        },
      },
    },
    {
      name: "active_status",
      label: "Status",
      options: {
        customBodyRenderLite: (dataIndex) => {
          const status = formData[dataIndex]?.active_status;

          return (
            <Switch
              size="small"
              color={status === 1 ? "success" : "error"}
              checked={status === 1 ? true : false}
              onChange={() => {
                dispatch(
                  showConfirmModal({
                    msg: status === 1 ? "deactivate" : "activate",
                    action: () =>
                      toggleStatus(formData[dataIndex]?.shop_details_id),
                  })
                );
              }}
              style={{ marginBottom: "5px" }}
            />
          );
        },
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            options: {
              customBodyRenderLite: (dataIndex) => {
                const status = formData[dataIndex]?.active_status;
                return (
                  <div className="actionBtns">
                    {permissions?.edit && (
                      <Button
                        disabled={status === 1 ? true : false}
                        aria-label="edit"
                        onClick={() => editClickHandler(formData[dataIndex])}
                      >
                        <EditIcon
                          fontSize="medium"
                          sx={{ color: status === 1 ? grey[600] : "secondary" }}
                        />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        disabled={status === 1 ? true : false}
                        aria-label="delete"
                        onClick={() => {
                          dispatch(
                            showConfirmModal({
                              msg: "delete",
                              action: () =>
                                deleteClickHandler(
                                  formData[dataIndex]?.shop_details_id
                                ),
                            })
                          );
                        }}
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{
                            color: status === 1 ? grey[600] : red[500],
                          }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];
  const handleClose = () => {
    setOpen(false);
    setOpenImage(false);
    setSelectedRow(null);
    reset();
  };
  const editClickHandler = (item) => {
    setOpen(true);
    setSelectedRow({ ...item });
  };
  const onSubmit = (data) => {
    saveForm(data);
  };

  const toggleStatus = (id) => {
    setLoader(true);
    axios
      .get("/shopSwitch/" + id)
      .then(function () {
        loadInitialData();
      })
      .catch(function (err) {
        toast.error(err?.message);
      })
      .finally(() => {
        setLoader(false);
      });
  };
  const saveForm = async (data) => {
    setLoader(true);
    try {
      let response;
      const regionObj = region?.find(
        (item) => item?.city_id == data?.region_id
      );

      data = {
        ...data,
        region_id: regionObj?.region_id,
        map_url: data.map_url?.startsWith("http")
          ? data.map_url
          : `https://maps.app.goo.gl/${data.map_url}`,
      };
      if (selectedRow?.shop_details_id) {
        response = await axios.post(
          "/updateShopDetails",
          { ...data, shop_details_id: selectedRow?.shop_details_id },
          data
        );
      } else {
        response = await axios.post("/createShopDetails", data);
      }

      if (!response?.data?.error) {
        toast.success(response?.data?.message);
        loadInitialData();
        setOpen(false);
        setSelectedRow(() => null);
        reset();
      } else {
        setOpen(false);
        let errDesc = "";
        if (response?.data?.data) {
          Object.values(response?.data?.data).map((error, index) => {
            errDesc =
              index === 0 ? errDesc + error + "\n " : errDesc + error + "\n ";
          });
        } else {
          errDesc = response?.data?.message;
        }

        dispatch(
          showModal({
            msg: errDesc,
            error: response?.data?.error,
          })
        );
      }
    } catch (error) {
      dispatch(
        showModal({
          msg: error?.message,
          error: true,
        })
      );
    } finally {
      setLoader(false);
      setOpen(false);
    }
  };

  const CreateEditDialog = React.memo(() => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"sm"} fullWidth>
        <DialogTitle className="text-primary">
          {selectedRow ? "Edit Shop" : "New Shop"}
        </DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={6}>
              <FormInputDropdown
                name="region_id"
                control={control}
                label={"City"}
                type="single"
                options={region?.map((item) => ({
                  id: item?.city?.id,
                  name: item?.city?.name,
                }))}
                errors={errors}
                defaultValue={selectedRow?.region?.[0]?.city_id}
                mandatory="true"
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputText
                name="shop_code"
                control={control}
                label="Shop Code"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.shop_code}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                name="shop_name"
                control={control}
                label="Shop Name"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.shop_name}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputTextArea
                name="address"
                control={control}
                label="Address"
                minRows={2}
                maxRows={4}
                placeholder={"Shop Address"}
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.address}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputText
                name="map_url"
                control={control}
                label="Google Map URL"
                placeholder="https://maps.app.goo.gl/..."
                errors={errors}
                defaultValue={
                  selectedRow?.map_url
                    ? selectedRow.map_url.replace(
                        "https://maps.app.goo.gl/",
                        ""
                      )
                    : ""
                }
              />
            </Grid>

            {/* ✅ Added: Latitude / Longitude — used by the storefront map
                embed on the shop detail page. Optional; existing shops with
                no coordinates keep working exactly as before. */}
            <Grid item xs={6}>
              <FormInputText
                name="latitude"
                control={control}
                label="Latitude"
                placeholder="9.9130375"
                errors={errors}
                defaultValue={selectedRow?.latitude}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputText
                name="longitude"
                control={control}
                label="Longitude"
                placeholder="78.1405183"
                errors={errors}
                defaultValue={selectedRow?.longitude}
              />
            </Grid>

            <Grid item xs={6}>
              <FormInputText
                name="mobile_no"
                control={control}
                label="Mobile No"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.mobile_no}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInputText
                name="pincode"
                control={control}
                label="Pincode"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.pincode}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            // disabled={!isValid}
            color="primary"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  const options = {
    selectableRows: false,
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    searchText: searchText,
    onSearchChange: (searchValue) => setSearchText(searchValue),
    enableNestedDataAccess: ".",
    rowsPerPage: pageSize,
    page: pageNo,
    filter: true,
    onChangeRowsPerPage: (numberOfRows) =>
      dispatch(
        handlePageSettings({
          screen: "shop",
          pageSize: numberOfRows,
          pageNo: pageNo,
        })
      ),
    onChangePage: (currentPage) =>
      dispatch(
        handlePageSettings({
          screen: "shop",
          pageSize: pageSize,
          pageNo: currentPage,
        })
      ),
  };
  const onCredentailsSubmit = (data) => {
    console.log(data);
  };

  const CredentialsDialog = () => {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth={"md"}>
        <DialogTitle className="text-primary">
          {selectedRow ? "Edit Category" : "New Category"}
        </DialogTitle>
        <DialogContent>
          <Grid container columnSpacing={{ xs: 5 }}>
            <Grid item xs={12}>
              <FormInputText
                name="r_key"
                control={control}
                label="Razorpay_key"
                errors={errors}
                mandatory="true"
                defaultValue={selectedRow?.razorpay_key}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="error">
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit(onCredentailsSubmit)}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Shop</h4>

          <MUIDataTable
            data={formData?.length > 0 ? formData : []}
            columns={columns}
            options={options}
            title={
              permissions.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedRow(null);
                    setOpen(true);
                    reset();
                  }}
                  icon={<AddIcon />}
                >
                  New Shop
                </Button>
              ) : null
            }
          />
        </>
      )}
      <CreateEditDialog />
      {/* <CredentialsDialog /> */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {selectedRow?.confirmLabel} ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="contained"
            color="error"
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => selectedRow?.action()}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onClose={() => setForgotPasswordOpen(false)}
        shop={selectedShop}
      />
    </>
  );
}

export default Shop;